// FieldMappingEditor — field_mapping 结构化表单编辑 (doc/042 §2-§4).
//
// 把绑定行的 field_mapping JSON textarea 换成运营可读的中文表单:每个已知键
// (fieldMappingKeys 白名单,第一批已在 pushProfiles.ts 声明)渲染为「中文标签 +
// 输入框(按 type 渲染 text/number/enum select)+ 说明文字」。规则:
//
//   - 表单为权威:任何行编辑即时重构 JSON 并推回 onChange
//   - 高级 JSON 折叠区:打开后草稿隔离,「应用 JSON」单向回写,未应用时有角标
//   - 未知键(远程模板 input 等)保真为「附加字段」行,key 锁定不可改,value 可改
//   - 无白名单适配器(custom-push)退化为自由键值行编辑,key/value 均可改
//   - 导入时非字符串原始值(number/bool)规范化为字符串(后端 Value 只收 string)
import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import {
  ADAPTER_PROFILES,
  validateFieldMapping,
  type FieldMappingKeySpec,
} from "@/adapters/pushProfiles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

type Row = { id: string; key: string; value: string };

export function FieldMappingEditor({
  adapterType,
  value,
  onChange,
  disabled,
}: {
  adapterType: string;
  value: string;
  onChange: (json: string) => void;
  disabled?: boolean;
}) {
  // specs 必须是稳定引用:此前每次渲染新建 [] 会让下方同步 effect 每渲染都跑一遍,
  // 在无白名单适配器(value 非空)时 setRows 触发 infinite update(doc/050 冒烟发现)。
  const specs = useMemo(() => ADAPTER_PROFILES[adapterType]?.fieldMappingKeys ?? [], [adapterType]);
  const specByKey = new Map(specs.map((s) => [s.key, s]));
  const hasWhitelist = specs.length > 0;
  // ezlink 的未知键是模板行输入(合法);deepnix/rocksoildai/flypig 后端会拒绝。
  const strictWhitelist = hasWhitelist && adapterType !== "custom-ezlinkai-com";

  const [rows, setRows] = useState<Row[]>(() => rebuildRows(specs, adapterType, "").rows);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [jsonText, setJsonText] = useState("");
  const [advancedDirty, setAdvancedDirty] = useState(false);
  const [advancedErr, setAdvancedErr] = useState<string | null>(null);
  const [applyWarn, setApplyWarn] = useState<string | null>(null);
  const [importWarns, setImportWarns] = useState<string[]>([]);

  // 外部 value / adapterType 变化 → 重建行。跳过「我们自己刚推回的值」以避免
  // 打字中途光标被重置(serialize 与内部状态同源时视为已同步)。
  const prevAdapter = useRef(adapterType);
  useEffect(() => {
    const adapterChanged = prevAdapter.current !== adapterType;
    prevAdapter.current = adapterType;
    const current = serialize(rows);
    if (!adapterChanged && value === current) return;
    const { rows: next, warns } = rebuildRows(specs, adapterType, value ?? "");
    setRows(next);
    if (warns.length > 0) setImportWarns(warns);
    setAdvancedDirty(false);
    setAdvancedErr(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, adapterType, specs]);

  // 高级区打开且未修改时,跟随表单刷新草稿;已修改则保留用户草稿。
  useEffect(() => {
    if (!advancedOpen || advancedDirty) return;
    const s = serialize(rows);
    if (s !== jsonText) setJsonText(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, advancedOpen, advancedDirty]);

  function commit(next: Row[]) {
    setRows(next);
    setApplyWarn(null);
    onChange(serialize(next));
  }

  const setValue = (id: string, v: string) => commit(rows.map((r) => (r.id === id ? { ...r, value: v } : r)));
  const setKey = (id: string, k: string) => commit(rows.map((r) => (r.id === id ? { ...r, key: k } : r)));
  const removeRow = (id: string) => commit(rows.filter((r) => r.id !== id));

  function addWhitelistField(key: string) {
    if (!key) return;
    const spec = specByKey.get(key);
    if (!spec) return;
    const next = rows.filter((r) => r.key !== key);
    const specIndex = specs.findIndex((s) => s.key === key);
    const insertIdx = next.findIndex((r) => {
      const rIdx = specs.findIndex((s) => s.key === r.key);
      return rIdx === -1 || specIndex < rIdx;
    });
    const at = insertIdx === -1 ? next.length : insertIdx;
    next.splice(at, 0, { id: `fm-${spec.key}-${next.length}-${Date.now()}`, key, value: "" });
    commit(next);
  }

  function addFreeRow() {
    commit([...rows, { id: `fm-free-${rows.length}-${Date.now()}`, key: "", value: "" }]);
  }

  function toggleAdvanced() {
    if (advancedOpen) {
      setAdvancedOpen(false);
      return;
    }
    setAdvancedOpen(true);
    setJsonText(serialize(rows));
    setAdvancedDirty(false);
    setAdvancedErr(null);
    setApplyWarn(null);
  }

  function onJsonText(t: string) {
    setJsonText(t);
    setAdvancedDirty(t.trim() !== serialize(rows));
    setAdvancedErr(null);
    setApplyWarn(null);
  }

  function applyJson() {
    const { errors, warnings } = validateFieldMapping(adapterType, jsonText);
    if (errors.length > 0) {
      setAdvancedErr(errors.join("; "));
      return;
    }
    const { rows: next, warns } = rebuildRows(specs, adapterType, jsonText);
    setRows(next);
    setAdvancedDirty(false);
    setAdvancedErr(null);
    const notes = [...warnings, ...warns];
    setApplyWarn(notes.length > 0 ? notes.join("; ") : null);
    onChange(serialize(next));
  }

  if (adapterType === "") {
    return <p className="text-[11px] text-muted-foreground">选择推送站后展示 field_mapping 表单。</p>;
  }

  const optionalCandidates = specs.filter((s) => !s.required && !rows.some((r) => r.key === s.key));
  const shownRows = rows;
  // 白名单外的键(模板 input / 自由键)排在最后(rebuildRows 已保证顺序,这里仅渲染)。

  return (
    <div className="space-y-3">
      <div className="space-y-2.5">
        {shownRows.map((r) => {
          const spec = specByKey.get(r.key);
          if (hasWhitelist) {
            return (
              <div key={r.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <label className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                    {spec ? spec.label : "附加字段"}
                    {spec ? (
                      spec.required ? (
                        <Badge variant="destructive">必填</Badge>
                      ) : (
                        <Badge variant="muted">可选</Badge>
                      )
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">附加</Badge>
                    )}
                    <span className="font-mono text-[10px] font-normal text-muted-foreground">({r.key})</span>
                  </label>
                  {!spec?.required && (
                    <button
                      type="button"
                      className="text-[10px] text-muted-foreground underline hover:text-foreground"
                      onClick={() => removeRow(r.id)}
                      title="删除该字段"
                    >
                      删除
                    </button>
                  )}
                </div>
                <FieldValueInput
                  spec={spec}
                  value={r.value}
                  onChange={(v) => setValue(r.id, v)}
                  disabled={disabled}
                />
                {(spec?.meaning || spec?.useCase || spec?.hint) && (
                  <div className="space-y-0.5">
                    {spec?.meaning && (
                      <p className="text-[11px] text-muted-foreground">
                        <span className="text-foreground/70">含义</span>:{spec.meaning}
                      </p>
                    )}
                    {spec?.useCase && (
                      <p className="text-[11px] text-muted-foreground">
                        <span className="text-foreground/70">适用场景</span>:{spec.useCase}
                      </p>
                    )}
                    {spec?.hint && <p className="text-[11px] text-muted-foreground">{spec.hint}</p>}
                  </div>
                )}
                {!spec && strictWhitelist && (
                  <p className="text-[11px] text-amber-600">
                    ⚠ key 不在该商家白名单,后端会拒绝该绑定;若要换成白名单键请删除后用「添加可选字段」重加。
                  </p>
                )}
                {!spec && hasWhitelist && !strictWhitelist && (
                  <p className="text-[11px] text-muted-foreground">模板行输入键,保留原样。</p>
                )}
                {spec?.type === "int" && r.value.trim() !== "" && !/^-?\d+$/.test(r.value.trim()) && (
                  <p className="text-[11px] text-destructive">{spec.label} 必须是整数(当前 "{r.value}")</p>
                )}
                {spec?.type === "decimal" && r.value.trim() !== "" && !/^\d+(\.\d{1,2})?$/.test(r.value.trim()) && (
                  <p className="text-[11px] text-destructive">{spec.label} 必须是数字,最多 2 位小数(当前 "{r.value}")</p>
                )}
              </div>
            );
          }
          return (
            <div key={r.id} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
              <Input
                className="h-8 font-mono text-xs"
                value={r.key}
                onChange={(e) => setKey(r.id, e.target.value)}
                placeholder="key"
                disabled={disabled}
                aria-label="字段 key"
              />
              <Input
                className="h-8 text-xs"
                value={r.value}
                onChange={(e) => setValue(r.id, e.target.value)}
                placeholder="value"
                disabled={disabled}
                aria-label="字段 value"
              />
              <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => removeRow(r.id)} aria-label="删除字段">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {hasWhitelist && optionalCandidates.length > 0 && (
        <select
          className="h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus:ring-2 focus:ring-ring"
          value=""
          onChange={(e) => addWhitelistField(e.target.value)}
          aria-label="添加可选字段"
        >
          <option value="">＋ 添加可选字段(从白名单选)</option>
          {optionalCandidates.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}({s.key})
            </option>
          ))}
        </select>
      )}
      {hasWhitelist && optionalCandidates.length === 0 && (
        <p className="text-[11px] text-muted-foreground">可选字段已全部添加。</p>
      )}
      {!hasWhitelist && (
        <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground underline hover:text-foreground" onClick={addFreeRow}>
          <Plus className="h-3 w-3" /> 添加字段
        </button>
      )}

      {importWarns.length > 0 && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />
          <span className="flex-1">导入时已规范化:{importWarns.join("; ")}</span>
          <button type="button" className="hover:text-amber-900" onClick={() => setImportWarns([])} aria-label="关闭提示">×</button>
        </div>
      )}

      <div className="rounded-md border bg-muted/20 p-2">
        <button type="button" className="flex w-full items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground" onClick={toggleAdvanced}>
          {advancedOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          高级:手动编辑 JSON
          {advancedOpen && advancedDirty && <span className="ml-1 rounded bg-amber-200 px-1 text-[10px] text-amber-800">未应用</span>}
        </button>
        {advancedOpen && (
          <div className="mt-2 space-y-2">
            <textarea
              className="min-h-[96px] w-full rounded-md border bg-background px-2 py-1.5 font-mono text-[11px] outline-none focus:ring-2 focus:ring-ring"
              value={jsonText}
              onChange={(e) => onJsonText(e.target.value)}
              spellCheck={false}
              aria-label="手动编辑 field_mapping JSON"
            />
            {advancedErr && <p className="text-[11px] text-destructive">{advancedErr}</p>}
            {applyWarn && <p className="text-[11px] text-amber-600">⚠ {applyWarn}</p>}
            <Button type="button" size="sm" variant="outline" className="h-7 text-[11px]" disabled={!advancedDirty} onClick={applyJson}>
              应用 JSON
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// FieldValueInput renders the value control by spec.type: enum → select,
// int/decimal → number 输入,其余 → 文本输入。未知键/自由键(spec 为空)一律文本输入。
function FieldValueInput({
  spec,
  value,
  onChange,
  disabled,
}: {
  spec: FieldMappingKeySpec | undefined;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  if (spec?.type === "enum" && spec.enum && spec.enum.length > 0) {
    const options = !value || spec.enum.includes(value) ? spec.enum : [value, ...spec.enum];
    return (
      <select
        className="h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus:ring-2 focus:ring-ring"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={spec.label}
      >
        <option value="">— 请选择 —</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {spec.enumLabels?.[o] ?? o}
          </option>
        ))}
      </select>
    );
  }
  const inputCls = "h-8 w-full rounded-md border bg-background px-2 text-xs outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";
  if (spec?.type === "int" || spec?.type === "decimal") {
    return (
      <input
        className={inputCls}
        type="number"
        inputMode={spec.type === "decimal" ? "decimal" : "numeric"}
        step={spec.type === "decimal" ? "0.01" : "1"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={spec.label}
      />
    );
  }
  return (
    <input
      className={inputCls}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={spec?.label ?? "字段值"}
    />
  );
}

// serialize 把行转成后端接受的 JSON:值一律字符串、空 key/空 value 条目跳过
// (与 backend fieldMappingEntry.Value string + omitempty 语义一致)。
function serialize(rows: Row[]): string {
  const entries = rows
    .filter((r) => r.key.trim() !== "" && r.value.trim() !== "")
    .map((r) => ({ key: r.key, value: r.value }));
  return JSON.stringify(entries, null, 2);
}

// rebuildRows 解析 JSON 并按稳定顺序重建行:必填(声明序)→ 已填可选(声明序) →
// 未知键(原顺序);缺失必填补空行;非字符串值规范化为字符串并记 warn。
function rebuildRows(specs: FieldMappingKeySpec[], adapterType: string, json: string): { rows: Row[]; warns: string[] } {
  const warns: string[] = [];
  let entries: { key: string; value: unknown }[] = [];
  const t = (json ?? "").trim();
  if (t !== "") {
    try {
      const p = JSON.parse(t);
      if (Array.isArray(p)) entries = p as { key: string; value: unknown }[];
    } catch {
      // 非法 JSON:按空处理,高级区「应用」已做防火墙,不会走到这里产生坏行。
    }
  }
  const byKey = new Map<string, unknown>();
  for (const e of entries) {
    const k = String(e?.key ?? "");
    if (k !== "" && !byKey.has(k)) byKey.set(k, e?.value);
  }
  let n = 0;
  const id = () => `fm-${adapterType}-${++n}`;
  const rows: Row[] = [];
  const used = new Set<string>();
  const push = (key: string, v: unknown) => {
    rows.push({ id: id(), key, value: normalizeValue(v, warns) });
    used.add(key);
  };
  for (const s of specs) {
    if (s.required) push(s.key, byKey.get(s.key));
  }
  for (const s of specs) {
    if (!s.required && (byKey.has(s.key) || s.visibleByDefault)) push(s.key, byKey.get(s.key));
  }
  for (const e of entries) {
    const k = String(e?.key ?? "");
    if (k !== "" && !used.has(k)) push(k, e?.value);
  }
  return { rows, warns };
}

function normalizeValue(v: unknown, warns: string[]): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  warns.push(`字段值 ${JSON.stringify(v)} 已规范化为字符串`);
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}
